import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, Plus, Minus, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SITE, breadcrumbSchema, faqSchema } from "@/lib/seo";
import { getGuide } from "@/lib/guides";

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E5DF]">
      <button onClick={() => setOpen((v) => !v)} data-testid={`guide-faq-${idx}`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left">
        <span className="font-body text-base text-[#1A1A1A]">{q}</span>
        {open ? <Minus size={18} className="text-[#B84A3B] flex-shrink-0" /> : <Plus size={18} className="text-[#B84A3B] flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-sm text-[#6E6E69] leading-relaxed max-w-2xl">{a}</p>}
    </div>
  );
};

export default function Guide() {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/" replace />;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.h1,
    description: g.metaDescription,
    datePublished: g.date,
    author: { "@type": "Organization", name: "Kurti Connection USA", url: SITE.url },
    publisher: { "@type": "Organization", name: "Kurti Connection USA", url: SITE.url },
  };

  return (
    <main data-testid="guide-page">
      <Seo title={g.seoTitle} description={g.metaDescription} path={`/guide/${slug}`}
        jsonLd={[articleSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: g.h1, path: `/guide/${slug}` }]), faqSchema(g.faqs)]} />

      <article className="max-w-[760px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="overline text-[#B84A3B] mb-3">{g.kicker}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">{g.h1}</h1>
        <div className="flex items-center gap-4 mt-4 text-[0.68rem] font-mono tracking-[0.12em] uppercase text-[#6E6E69]">
          <span className="flex items-center gap-1.5"><Calendar size={12} /> {g.date}</span>
          <span className="flex items-center gap-1.5"><Clock size={12} /> {g.readTime}</span>
        </div>
        <p className="mt-6 text-base font-body leading-relaxed text-[#1A1A1A] border-l-2 border-[#B84A3B] pl-5">{g.intro}</p>

        {g.sections.map((s, i) => (
          <Reveal key={s.h} delay={0.05}>
            <section className="mt-10" data-testid={`guide-section-${i}`}>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-3">{s.h}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="text-[0.95rem] font-body text-[#3a3a3a] leading-relaxed mt-3">{p}</p>
              ))}
            </section>
          </Reveal>
        ))}

        <div className="mt-12 bg-[#1A1A1A] text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="guide-cta">
          <div>
            <div className="font-display text-2xl tracking-tight">Ready to shop the look?</div>
            <p className="text-sm text-white/70 font-body mt-1">In-store in Schaumburg or shipped across the USA.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to={g.cta.to} data-testid="guide-cta-shop"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] px-6 py-3 text-[0.68rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors">
              {g.cta.label} <ArrowRight size={13} />
            </Link>
            <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi! I read your style guide and need help picking an outfit.")}`}
              target="_blank" rel="noreferrer" data-testid="guide-cta-whatsapp"
              className="inline-flex items-center gap-2 border border-white/40 px-6 py-3 text-[0.68rem] font-mono tracking-[0.18em] uppercase hover:border-[#25D366] hover:text-[#25D366] transition-colors">
              <MessageCircle size={13} /> Ask Us
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-2" data-testid="guide-links">
          {g.links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`guide-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[0.66rem] font-mono tracking-[0.15em] uppercase border border-[#E5E5DF] px-4 py-2 hover:border-[#B84A3B] hover:text-[#B84A3B] transition-colors">
              {l.label}
            </Link>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="font-display text-3xl tracking-tight mb-2">Common Questions</h2>
          {g.faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} idx={i} />)}
        </section>
      </article>
    </main>
  );
}
